import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { applyApiFieldErrors, useZodForm } from "@empite/app-kit";
import { Alert, Button, Card, Field, Input, PageHeader } from "@empite/ui-components";
import { useAdminSignIn, useAdminSignUp } from "../admin-hooks";
import { ErrorAlert } from "./guards";

/**
 * The only self-service way onto this realm — and it does not put anybody into
 * an EXISTING tenant. Signing up provisions a brand-new one: a tenant, its
 * owner, its default application suite and a free-tier subscription, in one
 * server-side transaction. A second seat in somebody else's company arrives by
 * invitation and never through this form.
 *
 * The validation here is a COURTESY, not the control. internal/admin's SignUp
 * re-checks every rule below — the address, the password policy, the length of
 * the company name and the length of the slug derived from it — and the route
 * is rate limited, because it is unauthenticated. Anything this form fails to
 * catch is refused there and shown verbatim in the banner.
 */
const schema = z.object({
  company_name: z
    .string()
    .min(1, "Tell us the name of your company.")
    // 120, matching internal/admin's maxCompanyName. The server ALSO bounds
    // the slug it derives from this at 63, which no length check here can
    // predict — a name that slugs too long comes back as a sentence saying so.
    .max(120, "A company name must be at most 120 characters."),
  email: z.string().min(1, "Enter your email.").email("That is not an email address."),
  display_name: z.string().max(120, "A name must be at most 120 characters."),
  password: z.string().min(1, "Choose a password."),
});

type Values = z.infer<typeof schema>;

export function SignUp() {
  const navigate = useNavigate();
  const signUp = useAdminSignUp();
  const signIn = useAdminSignIn();
  const [formError, setFormError] = useState<unknown>(null);
  // Set only when the account EXISTS and the sign-in that follows did not.
  // The two failures are nothing alike: one means try again, the other means
  // you already have an account and only need to get into it.
  const [provisioned, setProvisioned] = useState<string | null>(null);
  const form = useZodForm<Values>(schema, {
    defaultValues: { company_name: "", email: "", display_name: "", password: "" },
  });

  const submit = form.handleSubmit(async (values) => {
    setFormError(null);
    const email = values.email.trim();
    const displayName = values.display_name.trim();
    try {
      await signUp.mutateAsync({
        company_name: values.company_name.trim(),
        email,
        // Omitted rather than sent empty: the server names the operator after
        // the local part of their address when this is missing, and "" is not
        // a name.
        display_name: displayName === "" ? undefined : displayName,
        password: values.password,
      });
    } catch (err) {
      if (!applyApiFieldErrors(form.setError, err, ["company_name", "email", "password"])) {
        setFormError(err);
      }
      return;
    }
    // POST /signup returns no cookie by design, so the session comes from the
    // ordinary sign-in route — the one place the status check, the roles check
    // and the sign-in audit row live. A failure here has NOT lost the account.
    try {
      await signIn.mutateAsync({ email, password: values.password });
    } catch {
      setProvisioned(email);
      return;
    }
    await navigate({ to: "/" });
  });

  if (provisioned) {
    return (
      <>
        <PageHeader title="Your company is registered" />
        <Card>
          <Alert tone="warning" title="Signed up, but not signed in">
            The account for <strong>{provisioned}</strong> exists and its company is provisioned.
            Signing in straight afterwards did not work — use{" "}
            <Link to="/sign-in" search={{ next: undefined }}>the sign-in page</Link>.
          </Alert>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Register your company"
        description="Creates a new tenant with you as its owner, on the free tier. Everything you do in this console afterwards is confined to it."
      />
      <Card>
        <form onSubmit={submit} noValidate>
          <ErrorAlert error={formError} title="That did not register" />
          <Field
            label="Company name"
            htmlFor="company_name"
            hint="Becomes your tenant's name, and the handle it is addressed by."
            error={form.formState.errors.company_name?.message}
          >
            <Input
              id="company_name"
              autoComplete="organization"
              invalid={Boolean(form.formState.errors.company_name)}
              {...form.register("company_name")}
            />
          </Field>
          <Field label="Your email" htmlFor="email" error={form.formState.errors.email?.message}>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              invalid={Boolean(form.formState.errors.email)}
              {...form.register("email")}
            />
          </Field>
          <Field
            label="Your name"
            htmlFor="display_name"
            hint="Optional. Left blank, we use the first part of your email."
            error={form.formState.errors.display_name?.message}
          >
            <Input
              id="display_name"
              autoComplete="name"
              invalid={Boolean(form.formState.errors.display_name)}
              {...form.register("display_name")}
            />
          </Field>
          <Field
            label="Password"
            htmlFor="password"
            error={form.formState.errors.password?.message}
          >
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              invalid={Boolean(form.formState.errors.password)}
              {...form.register("password")}
            />
          </Field>
          <Button type="submit" busy={signUp.isPending || signIn.isPending}>
            Register
          </Button>
        </form>
        <Alert tone="info" title="Already have an account?">
          <Link to="/sign-in" search={{ next: undefined }}>Sign in</Link> instead. If a colleague
          invited you into their company, use the link they sent you — signing up here would give
          you a company of your own rather than a seat in theirs.
        </Alert>
      </Card>
    </>
  );
}
